import styles from './PromptConfiguration.module.css'
import EnableToggle from './EnableToggle'
import PromptEditor from './PromptEditor'
import ModelSelector from './ModelSelector'
import UndoIcon from '@mui/icons-material/Undo'

interface PromptConfigurationProps {
  // Prompt management state
  promptText: string
  setPromptText: (text: string) => void
  isEnabled: boolean
  isSaving: boolean
  existingPrompt: any
  lastSavedToDb: Date | null
  lastSavedToLocalStorage: Date | null
  hasUnsavedChanges: boolean
  selectedModel: string
  setSelectedModel: (model: string) => void
  savedModel: string
  lastSavedModelToLocalStorage: Date | null
  isTogglingEnabled: boolean

  // Actions
  handleSave: () => Promise<any>
  handleRevertToSaved: () => void
  handleToggleEnabled: (newValue: boolean) => Promise<any>
  getSaveButtonText: () => string
  formatTimestamp: (date: Date | null) => string
}

function PromptConfiguration({
  promptText,
  setPromptText,
  isEnabled,
  isSaving,
  existingPrompt,
  lastSavedToDb,
  lastSavedToLocalStorage,
  hasUnsavedChanges,
  selectedModel,
  setSelectedModel,
  savedModel,
  lastSavedModelToLocalStorage,
  isTogglingEnabled,
  handleSave,
  handleRevertToSaved,
  handleToggleEnabled,
  getSaveButtonText,
  formatTimestamp,
}: PromptConfigurationProps) {
  return (
    <div className={styles.dbConfigSection}>
      {/* Header with Title and Enable Toggle */}
      <div className={styles.configHeaderRow}>
        <h2 className={styles.sectionHeader}>Prompt Configuration</h2>
        <EnableToggle
          isEnabled={isEnabled}
          isTogglingEnabled={isTogglingEnabled}
          existingPrompt={existingPrompt}
          onToggle={handleToggleEnabled}
        />
      </div>

      {/* AI Prompt Editor */}
      <PromptEditor
        promptText={promptText}
        setPromptText={setPromptText}
        lastSavedToLocalStorage={lastSavedToLocalStorage}
        formatTimestamp={formatTimestamp}
      />

      {/* Bottom Row: AI Model and Revert to Saved Button */}
      <div className={styles.dbConfigBottomRow}>
        {/* AI Model Selection */}
        <ModelSelector
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          lastSavedModelToLocalStorage={lastSavedModelToLocalStorage}
          formatTimestamp={formatTimestamp}
        />

        {/* Revert to Saved Button */}
        <div className={styles.revertButtonWrapper}>
          <h3 className={styles.sectionLabel}>&nbsp;</h3>
          <button
            className={styles.revertButton}
            onClick={handleRevertToSaved}
            disabled={
              !existingPrompt ||
              isSaving ||
              (!hasUnsavedChanges && savedModel === selectedModel)
            }
          >
            <span className={styles.buttonWithIcon}>
              <UndoIcon fontSize='small' />
              Revert to Saved
            </span>
          </button>
        </div>
      </div>

      {/* Smart Save Button */}
      <div className={styles.saveButtonRow}>
        <button
          className={`${styles.saveButton} ${
            !hasUnsavedChanges && savedModel === selectedModel
              ? styles.saveButtonNoChanges
              : ''
          }`}
          onClick={handleSave}
          disabled={
            (!hasUnsavedChanges && savedModel === selectedModel) ||
            !promptText ||
            isSaving
          }
        >
          <span className={styles.saveButtonText}>
            {isSaving ? 'Saving...' : getSaveButtonText()}
          </span>
          {lastSavedToDb && !isSaving && (
            <span className={styles.saveButtonTimestamp}>
              Last saved: {formatTimestamp(lastSavedToDb)}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

export default PromptConfiguration
